import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from 'radix-ui';
import { ChevronDownIcon } from 'lucide-react';
import {
  FIELD_FORCE_ROLES,
  INITIATIVE_STATUSES,
  SEARCH_TERM_MAX_LENGTH,
  type CalendarQuery,
  type SalesCycle,
  type SortOrder,
} from '@complan/contracts';
import { CALENDAR_ORIENTATIONS, type CalendarOrientation } from '@complan/domain';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  BOOLEAN_KEYS,
  setBooleanDimension,
  setListDimension,
  setSearchTerm,
  type BooleanFilterKey,
  type ListFilterKey,
} from '@/shared/url/calendar-search-params';
import { FF_ROLE_LABEL_KEYS, STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import { booleanDimensionLabel, listDimensionLabel } from './filter-chips';
import { SavedViewMenu } from './SavedViewMenu';
import { MASTER_DATA_FILTER_KEYS, type CalendarFilterOptions } from './calendar-options';

/**
 * The filter bar (UI spec §3.6).
 *
 * Anatomy the spec fixes: a white strip above the content, 1px border, radius
 * `--radius-card`, `9px 12px` padding, flex-wrapping with a 9px gap; each control
 * is a 10.5px uppercase muted micro-label above a padded input of `min-width:
 * 112px`; and the right side carries `Reset` · `Save view` · a primary `Export`.
 *
 * This one bar is the single source of truth: every control mutates the URL search
 * params through the codec helpers, and the calendar KPIs, grid, chips and export
 * all read from that same state — no control keeps a copy, so no view can filter
 * independently (UI spec §3.6, R11.13). The year lives in the route path, so the
 * year control changes the path via `onYearChange`; every other control changes
 * the search string via `onSearchChange`.
 *
 * ## Control order, and the binding set
 *
 * §3.6 fixes the calendar's filter set and its order: **Year · Orientation · Sales
 * Cycle · Platform · Category · Status · FF role · Search**. Those eight lead the
 * bar, in that sequence, matching the click-dummy reference.
 *
 * They are not the whole set, though, and the difference is deliberate rather than
 * an oversight in either document. R11.2 through R11.6 require the Calendar_View to
 * *provide* filters for Channel, customer group, audience, Initiative Type, Owner,
 * priority, confidentiality and the six Field Force / dependency Booleans as
 * well. Dropping them to match the reference's eight would remove required
 * functionality, so they follow the binding eight in a second group -
 * present and URL-bound, visually subordinate.
 *
 * ## Every dimension offers named values
 *
 * The master-data dimensions once rendered as free-text identifier inputs, because
 * no option source was reachable by a calendar reader. `useCalendarFilterOptions`
 * closes that: `/api/master-data/:set/selectable` and `/api/users/selectable` are
 * both gated on `initiative:read`, so Platform, Channel, customer group, audience,
 * Category, Initiative Type, priority and Owner are real pickers showing names -
 * which is what R11.2 asks for and what the reference shows.
 */

export interface FilterBarProps {
  readonly query: CalendarQuery;
  readonly sort: SortOrder;
  readonly search: URLSearchParams;
  readonly salesCycles: readonly SalesCycle[];
  readonly resultCount: number | null;
  /**
   * The orientation, which §3.6 places second in the bar. It lives in the search
   * string like every other filter, but is applied through its own setter because
   * the grid reads it directly.
   */
  readonly orientation: CalendarOrientation;
  readonly onOrientationChange: (orientation: CalendarOrientation) => void;
  /** Named values for the master-data dimensions and Owner (R11.2-R11.4). */
  readonly options: CalendarFilterOptions;
  /** Mutate the search string (filters, sort); the year is not part of it. */
  readonly onSearchChange: (next: URLSearchParams) => void;
  /** Change the planning year, which is the route path segment (R11.1). */
  readonly onYearChange: (year: number) => void;
  /** Apply a Saved_View by navigating to its stored year and search (R11.11). */
  readonly onApplySavedView: (year: number, search: URLSearchParams) => void;
  /** Reset every filter to entry state (R11.1). */
  readonly onReset: () => void;
  /** Enqueue the filtered-view export of the current state (R13.6, R15.1). */
  readonly onExport: () => void;
  readonly isExporting: boolean;
}

/** The window of years the year control offers, centred on the current system year. */
function yearOptions(current: number): number[] {
  const start = current - 3;
  return Array.from({ length: 8 }, (_, i) => start + i);
}

export function FilterBar(props: FilterBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const {
    query,
    search,
    salesCycles,
    resultCount,
    orientation,
    onOrientationChange,
    options,
    onSearchChange,
    onYearChange,
    onApplySavedView,
    onReset,
    onExport,
    isExporting,
  } = props;

  const setList = useCallback(
    (key: ListFilterKey, values: readonly string[]) => {
      onSearchChange(setListDimension(search, key, values));
    },
    [onSearchChange, search],
  );

  const setBoolean = useCallback(
    (key: BooleanFilterKey, value: boolean | undefined) => {
      onSearchChange(setBooleanDimension(search, key, value));
    },
    [onSearchChange, search],
  );

  const salesCycleOptions = useMemo(
    () => salesCycles.map((cycle) => ({ value: cycle.id, label: cycle.name })),
    [salesCycles],
  );

  return (
    <div>
      <div
        role="search"
        aria-label={t('filter.searchLabel')}
        className="border-line bg-panel relative mb-[14px] flex flex-wrap items-end gap-[9px] rounded-[var(--radius-card)] border px-3 py-[9px] pr-[240px]"
      >
        {/* Right-aligned action cluster: Reset · Save view · primary Export (§3.6).
            Absolutely positioned at the box's top-right corner rather than left as
            the last flex child: the `details` disclosure below is `w-full`, which
            forces a wrap before any trailing sibling, so an in-flow cluster would
            always drop to its own row. Pinning it here keeps it in the corner
            regardless of how many filter controls wrap; `pr-[240px]` on the
            container reserves its width so no control runs underneath. */}
        <div className="absolute right-3 top-[9px] flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onReset}>
            {t('common.reset')}
          </Button>
          <SavedViewMenu
            query={query}
            sort={props.sort}
            onApplySavedView={onApplySavedView}
          />
          <Button size="sm" onClick={onExport} disabled={isExporting}>
            {isExporting ? t('filter.exporting') : t('filter.export')}
          </Button>
        </div>

        {/* ── The binding set of UI spec §3.6, in its stated order ────────── */}
        <Control label={t('filter.year')}>
          {(id) => (
            <SelectInput
              id={id}
              value={String(query.planningYear)}
              onChange={(value) => onYearChange(Number.parseInt(value, 10))}
            >
              {yearOptions(query.planningYear).map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </SelectInput>
          )}
        </Control>

        <Control label={t('calendar.orientation.group')}>
          {(id) => (
            <SelectInput
              id={id}
              value={orientation}
              onChange={(value: string) => onOrientationChange(value as CalendarOrientation)}
            >
              {CALENDAR_ORIENTATIONS.map((value) => (
                <option key={value} value={value}>
                  {t(ORIENTATION_LABEL_KEYS[value])}
                </option>
              ))}
            </SelectInput>
          )}
        </Control>

        <Control label={listDimensionLabel('salesCycleIds', t)}>
          {(id) => (
            <MultiSelect
              id={id}
              options={salesCycleOptions}
              selected={query.salesCycleIds}
              onChange={(values) => setList('salesCycleIds', values)}
            />
          )}
        </Control>

        <MasterDataControl
          filterKey="platformIds"
          query={query}
          options={options}
          onChange={setList}
        />

        <MasterDataControl
          filterKey="categoryIds"
          query={query}
          options={options}
          onChange={setList}
        />

        <Control label={listDimensionLabel('statuses', t)}>
          {(id) => (
            <MultiSelect
              id={id}
              options={INITIATIVE_STATUSES.map((s) => ({ value: s, label: t(STATUS_LABEL_KEYS[s]) }))}
              selected={query.statuses}
              onChange={(values) => setList('statuses', values)}
            />
          )}
        </Control>

        <Control label={listDimensionLabel('fieldForceRoles', t)}>
          {(id) => (
            <MultiSelect
              id={id}
              options={FIELD_FORCE_ROLES.map((r) => ({ value: r, label: t(FF_ROLE_LABEL_KEYS[r]) }))}
              selected={query.fieldForceRoles}
              onChange={(values) => setList('fieldForceRoles', values)}
            />
          )}
        </Control>

        <Control label={t('filter.dimension.search')}>
          {(id) => (
            <SearchTermInput
              id={id}
              value={query.search}
              onCommit={(term) => onSearchChange(setSearchTerm(search, term))}
            />
          )}
        </Control>

        {/* ── The dimensions R11.2-R11.6 additionally require ──────────────────
            Present because the requirements mandate them, after the binding
            eight because the reference layout leads with those. Collapsed
            behind a disclosure so the bar reads like the reference at rest
            rather than as twenty equal-weight controls. */}
        <details className="w-full">
          <summary className="text-label cursor-pointer select-none py-1 text-[10.5px] font-semibold uppercase tracking-wide">
            {t('filter.moreDimensions')}
          </summary>
          <div className="mt-2 flex flex-wrap items-end gap-[9px]">
            {MASTER_DATA_FILTER_KEYS.filter(
              (key) => key !== 'platformIds' && key !== 'categoryIds',
            ).map((key) => (
              <MasterDataControl
                key={key}
                filterKey={key}
                query={query}
                options={options}
                onChange={setList}
              />
            ))}

            <Control label={listDimensionLabel('ownerUserIds', t)}>
              {(id) => (
                <MultiSelect
                  id={id}
                  options={options.optionsByKey.get('ownerUserIds') ?? []}
                  selected={query.ownerUserIds}
                  onChange={(values) => setList('ownerUserIds', values)}
                />
              )}
            </Control>

            {BOOLEAN_KEYS.map((key) => (
              <Control key={key} label={booleanDimensionLabel(key, t)}>
                {(id) => (
                  <TriStateSelect
                    id={id}
                    value={query[key]}
                    onChange={(value) => setBoolean(key, value)}
                  />
                )}
              </Control>
            ))}
          </div>
        </details>
      </div>
      {resultCount !== null ? (
        <span className="sr-only" aria-live="polite">
          {t('filter.resultCountVisible', { count: resultCount })}
        </span>
      ) : null}
    </div>
  );
}

/** Orientation labels, resolved from the catalogue (R18.9). */
const ORIENTATION_LABEL_KEYS: Record<CalendarOrientation, string> = {
  month: 'calendar.orientation.month',
  'calendar-week': 'calendar.orientation.calendarWeek',
  'sales-cycle': 'calendar.orientation.salesCycle',
};

/**
 * One master-data filter dimension, rendered as a named multi-select.
 *
 * A single component for all seven so the label, the option source and the URL key
 * stay derived from one `filterKey` rather than being repeated per dimension - the
 * shape that previously let a control and its option list drift apart.
 *
 * A value the option list does not contain is still shown as selected: R11.15's
 * Saved_View case can name a record that has since been removed, and silently
 * dropping it from the summary would tell the reader the filter is narrower than it
 * is.
 */
function MasterDataControl({
  filterKey,
  query,
  options,
  onChange,
}: {
  readonly filterKey: (typeof MASTER_DATA_FILTER_KEYS)[number];
  readonly query: CalendarQuery;
  readonly options: CalendarFilterOptions;
  readonly onChange: (key: ListFilterKey, values: readonly string[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selected = query[filterKey].map(String);
  const available = options.optionsByKey.get(filterKey) ?? [];

  return (
    <Control label={listDimensionLabel(filterKey, t)}>
      {(id) => (
        <MultiSelect
          id={id}
          options={available}
          selected={selected}
          onChange={(values) => onChange(filterKey, values)}
        />
      )}
    </Control>
  );
}

/**
 * A single labelled control: 10.5px uppercase muted micro-label above the input
 * (§3.6). The label's own generated `id` is handed to `children` so the rendered
 * input can adopt it as its own `id` — an explicit `<label htmlFor>` /
 * `<input id>` pairing gives every control a real accessible name (`select-name`,
 * R18.6), rather than the wrapping `<div id>` this previously pointed `htmlFor`
 * at, which associates the label with nothing focusable.
 */
function Control({
  label,
  children,
}: {
  label: string;
  children: (id: string) => React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="flex min-w-[112px] flex-col gap-1">
      <label
        htmlFor={id}
        className="text-label text-[10.5px] font-semibold uppercase tracking-wide"
      >
        {label}
      </label>
      {children(id)}
    </div>
  );
}

const CONTROL_CLASS =
  'border-line bg-panel text-txt min-w-[112px] rounded-[var(--radius-control)] border px-[7px] py-[5px] text-[12px] outline-none focus-visible:border-accent-line';

function SelectInput({
  id,
  value,
  onChange,
  children,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <select
      id={id}
      className={CONTROL_CLASS}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {children}
    </select>
  );
}

/** A tri-state Boolean control: unfiltered / yes / no (the filter grammar's `null` | `true` | `false`). */
function TriStateSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: boolean | null;
  onChange: (value: boolean | undefined) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const asString = value === null ? '' : value ? 'true' : 'false';
  return (
    <select
      id={id}
      className={CONTROL_CLASS}
      value={asString}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === '' ? undefined : next === 'true');
      }}
    >
      <option value="">{t('common.any')}</option>
      <option value="true">{t('common.yes')}</option>
      <option value="false">{t('common.no')}</option>
    </select>
  );
}

interface Option {
  readonly value: string;
  readonly label: string;
  /** Marked in the menu as retained-but-inactive (R11.2, R11.15, R5.9). */
  readonly isInactive?: boolean;
}

/**
 * A multi-value select over a known option list, rendered as a compact
 * dropdown: a trigger button showing a summary of the current selection, and a
 * checkbox-per-option menu that opens on click/`Enter`/`Space` (Radix
 * `DropdownMenu`, the same primitive family `SavedViewMenu`'s popover already
 * uses in this codebase). Its whole value set is written to the URL on change,
 * so it holds no state of its own beyond the menu's own open/closed state.
 *
 * Replaces the previous always-expanded native `<select multiple>`, which
 * rendered every option stacked open at all times rather than as a real
 * dropdown a user opens and closes. `DropdownMenu.CheckboxItem` keeps the
 * control keyboard-operable — `Enter`/`Space` opens it, arrow keys move
 * between options, `Enter`/`Space` toggles the focused one, `Escape` closes
 * and returns focus to the trigger — without hand-rolling that behaviour.
 *
 * Takes its accessible name from the `Control` label via `id` (`select-name`),
 * so the trigger button — not a `<select>` — is the element the label points
 * at; `htmlFor`/`id` on a `<button>` still associates the label as its
 * accessible name.
 */
function MultiSelect({
  id,
  options,
  selected,
  onChange,
}: {
  id: string;
  options: readonly Option[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        onChange([...selected, value]);
      } else {
        onChange(selected.filter((v) => v !== value));
      }
    },
    [onChange, selected],
  );

  const selectedLabels = options
    .filter((option) => selectedSet.has(option.value))
    .map((option) => option.label);
  const summary =
    selectedLabels.length === 0
      ? t('filter.multiSelectAny')
      : selectedLabels.length <= 2
        ? selectedLabels.join(', ')
        : t('filter.multiSelectCount', { count: selectedLabels.length });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          id={id}
          ref={triggerRef}
          type="button"
          className={cn(
            CONTROL_CLASS,
            'flex items-center justify-between gap-1 text-left',
          )}
        >
          <span className="truncate">{summary}</span>
          <ChevronDownIcon className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="border-line bg-panel z-50 max-h-[280px] min-w-[180px] overflow-y-auto rounded-[var(--radius-card)] border p-1 shadow-[var(--shadow-drawer)]"
          // Explicitly restores focus to the trigger on close rather than
          // relying solely on Radix's own `onCloseAutoFocus` default: that
          // default reads the trigger from context at the time the *content*
          // mounts, and in this checkbox-menu usage (content held open across
          // several toggles, closed only by `Escape`/outside-click rather than
          // an item `onSelect`) restoring via a ref this component owns is the
          // reliable path back to exactly the button the user opened the menu
          // from — the same "focus returns to the originating control" contract
          // every other dismissible surface in this file (`SavedViewMenu`'s
          // popover, the Detail_Panel) already honours.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          {options.length === 0 ? (
            <div className="text-muted-foreground px-2 py-1.5 text-[12px]">{t('filter.noCycles')}</div>
          ) : (
            options.map((option) => {
              const checked = selectedSet.has(option.value);
              return (
                <DropdownMenu.CheckboxItem
                  key={option.value}
                  checked={checked}
                  onCheckedChange={(next) => toggle(option.value, next)}
                  onSelect={(event) => event.preventDefault()}
                  className="text-txt flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-[12px] outline-none data-[highlighted]:bg-accent-soft"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'border-line flex size-[14px] shrink-0 items-center justify-center rounded-[3px] border',
                      checked && 'border-accent-line bg-accent-hover',
                    )}
                  >
                    {checked ? <CheckMark /> : null}
                  </span>
                  <span className="truncate">{option.label}</span>
                  {option.isInactive === true ? (
                    <span className="text-muted-foreground shrink-0 text-[10.5px]">
                      {t('filter.inactiveMarker')}
                    </span>
                  ) : null}
                </DropdownMenu.CheckboxItem>
              );
            })
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** A minimal inline checkmark glyph for a checked `MultiSelect` option, decorative (its state is conveyed by `aria-checked` on the menu item itself). */
function CheckMark(): React.JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="size-[10px] fill-none stroke-white stroke-[2px]">
      <path d="M3 8.5 6 11.5 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** How long the Search box waits after the last keystroke before filtering. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The free-text Search box (UI spec §3.6).
 *
 * Debounced, so typing a term issues one calendar read after a pause rather than
 * one per keystroke, each of which would also push a browser history entry. The
 * committed term goes to the URL like every other control, so the grid, the count,
 * the chips and the export all follow from it.
 *
 * The draft is local while typing and re-seeded whenever the committed value
 * changes from outside — a `Reset`, a Saved_View being applied, the chip's remove
 * control, or the back button — so the box always shows the term the calendar was
 * actually filtered by.
 */
function SearchTermInput({
  id,
  value,
  onCommit,
}: {
  id: string;
  value: string | null;
  onCommit: (term: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    const trimmed = draft.trim();
    // `null` and `''` both mean unfiltered here, so comparing the trimmed draft
    // against the committed value keeps an empty box from committing on mount.
    if (trimmed === (value ?? '')) {
      return;
    }
    const timer = setTimeout(() => onCommit(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, value, onCommit]);

  return (
    <input
      id={id}
      type="search"
      className={CONTROL_CLASS}
      value={draft}
      maxLength={SEARCH_TERM_MAX_LENGTH}
      placeholder={t('filter.searchPlaceholder')}
      onChange={(event) => setDraft(event.target.value)}
    />
  );
}

